import React from "react";
import { observer } from "mobx-react-lite";
import MainLayout from "layouts/MainLayout";
import { useParams } from "react-router";
import Filters from "features/ProductSearch/Filters/index.tsx";
import LinearLoading from "components/LinearLoading";

import CustomProducts from "features/ProductSearch/tabs/CustomProducts.tsx";
import SearchBar from "features/ProductSearch/SearchBar.tsx";
import {
  FiltersOptions,
  searchStore,
  SearchTabsProps,
} from "features/ProductSearch/SearchStore.ts";
import { useTranslation } from "react-i18next";
import CustomComponents from "features/ProductSearch/tabs/CustomComponents.tsx";
import { ORGANIZATIONAL_SEARCH_TABS_LIST } from "features/MappingEditor/consts.ts";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import DefaultProducts from "../../features/ProductSearch/tabs/DefaultProducts.tsx";
import DefaultComponents from "../../features/ProductSearch/tabs/DefaultComponents.tsx";
import SearchTabList from "../../features/ProductSearch/SearchTabList.tsx";
import { LicenseAppType } from "../../api-client";
import UsedProducts from "features/ProductSearch/tabs/UsedProducts.tsx";
import clsx from "clsx";
import useFeatureFlag from "hooks/useFeatureFlag.ts";
import LoadingIcon from "components/LoadingIcon/index.tsx";
import useProductSearch from "features/ProductSearch/useProductSearch.ts";
import EPDSearchContent from "features/EPDSearchModal/features/EPDSearchContent.tsx";
import EPDSearchTitle from "features/EPDSearchModal/components/ModalTitle.tsx";
import { buildingStore } from "store/Building/BuildingStore.ts";
import AdvancedProductSearch from "features/AdvancedProductSearch/index.tsx";

const OrganisationProductList = () => {
  const { organisation_id } = useParams();
  const { t } = useTranslation();
  const [query, setQuery] = React.useState<string>("");
  const [queryLoaded, setQueryLoaded] = React.useState(false);

  const [filters, setFilters] = React.useState<FiltersOptions>({
    DIN_categories: [],
    materials: [],
    conformity: [],
    certifications: [],
    other_properties: [],
  });

  React.useEffect(() => {
    setQuery(searchStore.openProductSearch.term || "");
    setQueryLoaded(!searchStore.openEPDSearch.state);

    return () => setQueryLoaded(false);
  }, [searchStore.openEPDSearch.state]);

  React.useEffect(() => {
    return () => {
      searchStore.setOpenEPDSearch({ state: false, term: "" });
      searchStore.setOpenProductSearch({ state: false, term: "" });
    };
  }, []);

  const { isFeatureVisible, isFeatureFlagLoading } = useFeatureFlag();
  const { initialCheck, loading, setLoading } = useProductSearch(
    ORGANIZATIONAL_SEARCH_TABS_LIST
  );

  const filteredTabList = isFeatureVisible("used_products")
    ? ORGANIZATIONAL_SEARCH_TABS_LIST
    : ORGANIZATIONAL_SEARCH_TABS_LIST.filter(
        (tab) => tab.id !== "used_products"
      );

  const onLoading = async () => {
    setLoading(true);
    buildingStore.setCurrentBuilding(undefined);
    buildingStore.setBuildingID(undefined);
    if (isFeatureFlagLoading) return;
    await initialCheck(isFeatureVisible("used_products"));
    setLoading(false);
  };

  React.useEffect(() => {
    (async () => await onLoading())();
  }, [isFeatureFlagLoading]);

  const onSearch = (value: string | number) => {
    searchStore.setCurrentSearchPage(1);
    setQuery(String(value));
  };

  function showSelectedTabContent() {
    switch (searchStore.selectedTab?.id) {
      case ORGANIZATIONAL_SEARCH_TABS_LIST[0].id:
        return <UsedProducts />;
      case ORGANIZATIONAL_SEARCH_TABS_LIST[1].id:
        return <CustomProducts />;
      case ORGANIZATIONAL_SEARCH_TABS_LIST[2].id:
        return <CustomComponents />;
      case ORGANIZATIONAL_SEARCH_TABS_LIST[3].id:
        return <DefaultProducts />;
      case ORGANIZATIONAL_SEARCH_TABS_LIST[4].id:
        return <DefaultComponents />;
      default:
        return <CustomProducts />;
    }
  }

  const onClickTab = (tab: SearchTabsProps) => {
    searchStore.setIsUsedSearch(tab.id === "used_products");
    searchStore.setSelectedTab(tab);
    searchStore.setCurrentSearchPage(1);
  };

  function updateFilters(name: string, values: unknown[]) {
    setFilters((state) => ({ ...state, [name]: values }));
  }

  const isManufacturerLicense =
    sidebarMenuStore.getCurrentLicense() === LicenseAppType.Manufacturer;

  const sameOrganization =
    sidebarMenuStore.currentOrganisation?.id == organisation_id;

  function tabContent(): React.ReactNode {
    /**
        We are waiting until this flag is set, and the organisation is the same and Feature flag is checked
        because a request get sent when loading the components with the previous settings.
       **/
    const showTabContent =
      searchStore.usingOrganisationSearch &&
      sameOrganization &&
      searchStore.isUsedSearch !== undefined &&
      !!searchStore.selectedTab?.id &&
      queryLoaded;

    if (!showTabContent) return null;

    return (
      <>
        <Filters
          query={query}
          filters={filters}
          setFilters={updateFilters}
          containerClassName={searchStore.isUsedSearch === true ? "hidden" : ""}
        />
        <div
          className={clsx("flex-grow bg-white", {
            "pt-5": searchStore.isUsedSearch === true,
          })}
        >
          {searchStore.productSearchLoading ? <LinearLoading /> : null}
          <div className="flex flex-col space-y-3">
            {showSelectedTabContent()}
          </div>
        </div>
      </>
    );
  }

  const productSearchView = () => {
    if (isFeatureVisible("new_product_search"))
      return <AdvancedProductSearch />;

    return (
      <>
        <SearchBar
          value={query}
          onChange={onSearch}
          searchBarStyle="w-full mr-2 md:min-w-[200px]"
        />
        <SearchTabList
          tabs={filteredTabList}
          onClickTab={onClickTab}
          hideConcularProducts={isManufacturerLicense}
        />
        {tabContent()}
      </>
    );
  };

  const epdSearchView = () => {
    return (
      <EPDSearchContent
        open={searchStore.openEPDSearch.state}
        close={() => searchStore.setOpenEPDSearch({ state: false, term: "" })}
        searchTerm={searchStore.openEPDSearch.term}
        generate
      />
    );
  };

  function getHeader(): React.ReactNode {
    return (
      <div className="flex items-center">
        {searchStore.openEPDSearch.state ? (
          <EPDSearchTitle />
        ) : (
          t("dashboard.productList")
        )}
      </div>
    );
  }

  return (
    <MainLayout
      headerTitle={getHeader()}
      className="overflow-y-auto bg-white p-6 !max-h-[calc(100vh-64px)] relative"
    >
      {loading && (
        <div className="absolute top-0 h-full left-0 flex w-full items-center justify-center bg-white z-3">
          <LoadingIcon type="secondary" className="!w-20 !h-20" />
        </div>
      )}

      {searchStore.openEPDSearch.state ? epdSearchView() : productSearchView()}
    </MainLayout>
  );
};

export default observer(OrganisationProductList);
